import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../../supabaseClient";
import LoaderBox from "../HomePage/LoaderBox";

const FSHomeFinished = () => {
  const [turniri, setTurniri] = useState([]);
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();

  const handleShare = async (e, turnir) => {
    e.stopPropagation();

    const shareUrl = `${window.location.origin}/${turnir.slug}`;

    if (navigator.share) {
      try {
        await navigator.share({
          title: turnir.naziv,
          text: `Provjeri turnir ${turnir.naziv} na FucaScore!`,
          url: shareUrl,
        });
      } catch (error) {
        /* console.log("Dijeljenje prekinuto:", error); */
      }
    } else {
      try {
        await navigator.clipboard.writeText(shareUrl);
        alert("Link kopiran u međuspremnik!");
      } catch (error) {
        console.error("Greška pri kopiranju:", error);
      }
    }
  };

  useEffect(() => {
    const fetchZavrseniTurniri = async () => {
      try {
        const { data: turniriData, error: turniriError } = await supabase
          .from("admin_turniri")
          .select(
            "id, naziv, slug, logo_url, datum_od, datum_do, status, total_fund",
          )
          .eq("show_turnir", true);

        if (turniriError) throw turniriError;

        const { data: utakmiceData, error: utakmiceError } = await supabase
          .from("turniri_utakmice")
          .select(
            `
            *,
            team_a:team_a_id(name),
            team_b:team_b_id(name)
          `,
          )
          .eq("is_completed", true);

        if (utakmiceError) throw utakmiceError;

        // Ispravljeno s 'turniri_ekipe' na 'turniri_timovi'
        const { data: timoviData } = await supabase
          .from("turniri_timovi")
          .select("id, name, turnir_id, tournament_id");

        const timoviMap = new Map(
          (timoviData || []).map((t) => [t.id, t.name]),
        );

        const now = new Date();

        const obradjeniTurniri = (turniriData || [])
          .map((turnir) => {
            const tId = turnir.id;

            let isAfterEnd = false;
            if (turnir.datum_do) {
              const endDate = new Date(turnir.datum_do);
              endDate.setHours(23, 59, 59, 999);
              if (now > endDate) {
                isAfterEnd = true;
              }
            }

            const turnirUtakmice = (utakmiceData || []).filter(
              (m) => m.turnir_id === tId || m.tournament_id === tId,
            );

            let dynamicWinner = null;

            if (turnirUtakmice.length > 0) {
              const finalMatch =
                turnirUtakmice
                  .filter(
                    (m) =>
                      m.bracket_slot_id !== 16 &&
                      m.bracket_slot_id !== 4 &&
                      m.bracket_slot_id !== 8,
                  )
                  .reduce((max, m) => {
                    return (m.bracket_slot_id || 0) >
                      (max?.bracket_slot_id || 0)
                      ? m
                      : max;
                  }, null) ||
                turnirUtakmice.find((m) => {
                  const group = (m.group_name || "").toLowerCase();
                  return group.includes("finale") && !group.includes("3");
                });

              if (
                finalMatch &&
                (isAfterEnd || turnir.status === "zavrsen") &&
                finalMatch.is_completed // Provjera je li utakmica uopće označena kao završena
              ) {
                if (finalMatch.scorea > finalMatch.scoreb) {
                  dynamicWinner =
                    finalMatch.team_a?.name ||
                    timoviMap.get(finalMatch.team_a_id);
                } else if (finalMatch.scoreb > finalMatch.scorea) {
                  dynamicWinner =
                    finalMatch.team_b?.name ||
                    timoviMap.get(finalMatch.team_b_id);
                }
              }
            }

            const isFinished = turnir.status === "zavrsen" || !!dynamicWinner;

            return {
              ...turnir,
              pobjednik_turnira: dynamicWinner || turnir.pobjednik || null,
              isFinished,
            };
          })
          .filter((t) => t.isFinished)
          .sort((a, b) => {
            const dateA = new Date(a.datum_do || a.datum_od || 0);
            const dateB = new Date(b.datum_do || b.datum_od || 0);
            return dateB - dateA;
          });

        setTurniri(obradjeniTurniri);
      } catch (err) {
        console.error("Greška pri dohvatu završenih turnira:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchZavrseniTurniri();
  }, []);

  const formatDateRange = (datumOd, datumDo) => {
    if (!datumOd) return "";
    const format = (dStr) => {
      const [year, month, day] = dStr.split("-");
      return `${day}.${month}.${year}.`;
    };
    if (!datumDo) return format(datumOd);
    return `${format(datumOd)} — ${format(datumDo)}`;
  };

  if (loading) return <LoaderBox text="Učitavanje završenih turnira..." />;

  if (turniri.length === 0) {
    return (
      <div className="fs-home-finished-empty">
        <i className="fi fi-rs-trophy"></i>
        <h3>Nema završenih turnira</h3>
        <p>Trenutno nema završenih turnira u sustavu.</p>
      </div>
    );
  }

  return (
    <div className="fs-home-finished-section">
      {/* HEADER */}
      <div className="fs-home-finished-section-header">
        <div className="fs-home-finished-title">
          <span className="title-line" />
          <h3>Završeni turniri</h3>
        </div>

        <div className="fs-home-finished-live-indicator">
          <span className="pulse-dot" />
          Pobjednici
        </div>
      </div>

      {/* CARDS */}
      <div className="fs-home-finished-stack">
        {turniri.map((turnir) => (
          <article
            key={turnir.id}
            className="fs-home-finished-card"
            onClick={() => navigate(`/${turnir.slug}`)}
          >
            <div className="fs-finished-card-glow" />

            {/* TOP */}
            <div className="fs-finished-top">
              <div className="fs-finished-completed">
                <span className="completed-dot" />
                ZAVRŠEN
              </div>

              <div className="fs-finished-date">
                <i className="fa-regular fa-calendar-days" />
                <span>{formatDateRange(turnir.datum_od, turnir.datum_do)}</span>
              </div>

              <button
                className="fs-finished-share"
                onClick={(e) => handleShare(e, turnir)}
                title="Podijeli turnir"
                type="button"
              >
                <i className="fi fi-rs-share" />
              </button>
            </div>

            {/* TOURNAMENT */}
            <div className="fs-finished-tournament">
              <div className="fs-finished-logo">
                <div className="logo-glow" />

                <img
                  src={turnir.logo_url || "default-logo-path.png"}
                  alt={turnir.naziv}
                />
              </div>

              <div className="fs-finished-tournament-info">
                <span className="tournament-label">TURNIR</span>

                <h4>{turnir.naziv}</h4>
              </div>
            </div>

            {/* WINNER */}
            <div className="fs-finished-winner">
              <div className="winner-icon">
                <i className="fi fi-rs-trophy"></i>
              </div>

              <div className="winner-info">
                <span>POBJEDNIK</span>

                <strong>
                  {turnir.pobjednik_turnira
                    ? turnir.pobjednik_turnira.toUpperCase()
                    : "NEPOZNATO"}
                </strong>
              </div>

              {turnir.total_fund && Number(turnir.total_fund) > 0 && (
                <div className="winner-fund">
                  <span>NAGRADNI FOND</span>

                  <strong>
                    {Number(turnir.total_fund).toLocaleString("de-DE")} €
                  </strong>
                </div>
              )}
            </div>
          </article>
        ))}
      </div>
    </div>
  );
};

export default FSHomeFinished;
